/**
 * Period detail page — summary of a single assessment period with quick actions
 */

import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { penilaianApi } from '../api/penilaianApi'
import type { AssessmentPeriod, PeriodStatus } from '../types/penilaian.types'

const STATUS_STYLES: Record<PeriodStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  open: 'bg-green-100 text-green-700',
  closed: 'bg-red-100 text-red-700',
}

const STATUS_LABELS: Record<PeriodStatus, string> = {
  draft: 'Draft',
  open: 'Aktif',
  closed: 'Ditutup',
}

function EditPeriodModal({
  period,
  onClose,
}: {
  period: AssessmentPeriod
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    start_date: period.start_date ?? '',
    end_date: period.end_date ?? '',
    submission_deadline: period.submission_deadline ?? '',
  })

  const mutation = useMutation({
    mutationFn: () =>
      penilaianApi.updatePeriod(period.id, {
        start_date: form.start_date || undefined,
        end_date: form.end_date || undefined,
        submission_deadline: form.submission_deadline || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['penilaian-period', period.id] })
      qc.invalidateQueries({ queryKey: ['penilaian-periods'] })
      toast.success('Periode berhasil diperbarui')
      onClose()
    },
    onError: (e: any) =>
      toast.error(e?.error?.message || 'Gagal menyimpan'),
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Edit Periode</h3>
          <p className="text-xs text-gray-500 mt-0.5">{period.name}</p>
        </div>

        <form
          id="edit-period-form"
          onSubmit={(e) => { e.preventDefault(); mutation.mutate() }}
          className="px-6 py-4 space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tanggal Mulai
            </label>
            <input
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tanggal Selesai
            </label>
            <input
              type="date"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Deadline Pengumpulan
            </label>
            <input
              type="date"
              value={form.submission_deadline}
              onChange={(e) => setForm({ ...form, submission_deadline: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">Kosongkan jika tidak ada deadline</p>
          </div>
        </form>

        <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
          <button
            type="submit"
            form="edit-period-form"
            disabled={mutation.isPending}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
          >
            {mutation.isPending ? 'Menyimpan…' : 'Simpan'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
          >
            Batal
          </button>
        </div>
      </div>
    </div>
  )
}

export default function PeriodDetailPage() {
  const { periodId } = useParams<{ periodId: string }>()
  const qc = useQueryClient()
  const [showEdit, setShowEdit] = useState(false)

  const { data: period, isLoading: periodLoading } = useQuery({
    queryKey: ['penilaian-period', periodId],
    queryFn: () => penilaianApi.getPeriod(periodId!),
    enabled: !!periodId,
  })

  const { data: kpisData } = useQuery({
    queryKey: ['penilaian-kpis', periodId],
    queryFn: () => penilaianApi.getPeriodKPIs(periodId!),
    enabled: !!periodId,
  })

  const { data: overviewData } = useQuery({
    queryKey: ['penilaian-overview', periodId],
    queryFn: () => penilaianApi.getEmployeeOverview(periodId!),
    enabled: !!periodId,
  })

  const openMut = useMutation({
    mutationFn: () => penilaianApi.updatePeriod(periodId!, { status: 'open' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['penilaian-period', periodId] })
      qc.invalidateQueries({ queryKey: ['penilaian-periods'] })
      toast.success('Periode dibuka')
    },
    onError: (e: any) => toast.error(e?.error?.message || 'Gagal'),
  })

  const closeMut = useMutation({
    mutationFn: () => penilaianApi.updatePeriod(periodId!, { status: 'closed' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['penilaian-period', periodId] })
      qc.invalidateQueries({ queryKey: ['penilaian-periods'] })
      toast.success('Periode ditutup')
    },
    onError: (e: any) => toast.error(e?.error?.message || 'Gagal'),
  })

  if (periodLoading) {
    return <div className="max-w-4xl mx-auto px-4 py-12 text-center text-gray-500">Memuat...</div>
  }

  if (!period) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <p className="text-gray-500 mb-4">Periode tidak ditemukan.</p>
        <Link to="/penilaian/periods" className="text-blue-600 hover:underline text-sm">
          ← Kembali ke daftar periode
        </Link>
      </div>
    )
  }

  const kpis = kpisData ?? []
  const rows = overviewData?.data ?? []
  const totalEmployees = rows.length

  // Count employees that have at least one KPI assigned (approximate from overview)
  const assignmentStats = rows.reduce(
    (acc, row) => {
      const atasan = row.assignments.filter((a) => a.assessor_role === 'ATASAN')
      const peer = row.assignments.filter((a) => a.assessor_role === 'PEER')
      const submitted = row.assignments.filter((a) => a.status === 'submitted')
      if (atasan.length > 0) acc.withAtasan++
      if (peer.length > 0) acc.withPeer++
      acc.totalAssignments += row.assignments.length
      acc.submitted += submitted.length
      return acc
    },
    { withAtasan: 0, withPeer: 0, totalAssignments: 0, submitted: 0 }
  )

  const submissionPct =
    assignmentStats.totalAssignments > 0
      ? Math.round((assignmentStats.submitted / assignmentStats.totalAssignments) * 100)
      : 0

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/penilaian/periods" className="hover:text-gray-700">
          Periode
        </Link>
        <span>/</span>
        <span className="text-gray-900 font-medium">{period.name}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-gray-900">{period.name}</h1>
            <span
              className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                STATUS_STYLES[period.status]
              }`}
            >
              {STATUS_LABELS[period.status]}
            </span>
          </div>
          <p className="text-sm text-gray-500">
            {period.start_date} — {period.end_date}
            {period.submission_deadline && (
              <span className="ml-2 text-orange-500">
                · Deadline: {period.submission_deadline}
              </span>
            )}
          </p>
        </div>

        {/* Status actions */}
        <div className="flex gap-2">
          {period.status !== 'closed' && (
            <button
              onClick={() => setShowEdit(true)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium flex items-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Edit
            </button>
          )}
          {period.status === 'draft' && (
            <button
              onClick={() => openMut.mutate()}
              disabled={openMut.isPending}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
            >
              {openMut.isPending ? 'Memproses...' : 'Buka Periode'}
            </button>
          )}
          {period.status === 'open' && (
            <button
              onClick={() => closeMut.mutate()}
              disabled={closeMut.isPending}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
            >
              {closeMut.isPending ? 'Memproses...' : 'Tutup Periode'}
            </button>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <StatCard label="Karyawan" value={totalEmployees} />
        <StatCard label="Definisi KPI" value={kpis.length} />
        <StatCard
          label="Total Penugasan"
          value={assignmentStats.totalAssignments}
          sub={`${assignmentStats.withAtasan} punya atasan · ${assignmentStats.withPeer} punya peer`}
        />
        <StatCard
          label="Sudah Submit"
          value={`${assignmentStats.submitted}/${assignmentStats.totalAssignments}`}
          sub={`${submissionPct}% selesai`}
          highlight={submissionPct === 100}
        />
      </div>

      {/* Quick actions */}
      <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
        Kelola Periode Ini
      </h2>
      {period.status === 'closed' && (
        <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 15v2m0 0v2m0-2h2m-2 0H10m2-11a9 9 0 110 18 9 9 0 010-18z" />
          </svg>
          Periode ini sudah ditutup. Pengeditan tidak diperbolehkan.
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <ActionCard
          icon={
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          }
          title="Manajemen KPI"
          description="Tambah dan atur KPI per karyawan"
          href={`/penilaian/kpis?period=${periodId}`}
          badge={kpis.length > 0 ? `${kpis.length} KPI` : undefined}
          disabled={period.status === 'closed'}
        />
        <ActionCard
          icon={
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
            />
          }
          title="Penugasan Penilai"
          description="Atur siapa menilai siapa"
          href={`/penilaian/assignments?period=${periodId}`}
          badge={
            assignmentStats.totalAssignments > 0
              ? `${assignmentStats.totalAssignments} penugasan`
              : undefined
          }
          disabled={period.status === 'closed'}
        />
        <ActionCard
          icon={
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          }
          title="Hasil Penilaian"
          description="Lihat skor dan proses approval"
          href={`/penilaian/results?period=${periodId}`}
        />
      </div>

      {showEdit && (
        <EditPeriodModal period={period} onClose={() => setShowEdit(false)} />
      )}

      {/* Assignment progress table */}
      {rows.length > 0 && (
        <>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Progress Karyawan
          </h2>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Karyawan</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 w-24">Atasan</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 w-24">Peer</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 w-32">Progress</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map(({ employee, assignments }) => {
                  const total = assignments.length
                  const done = assignments.filter((a) => a.status === 'submitted').length
                  const pct = total > 0 ? Math.round((done / total) * 100) : 0
                  const hasAtasan = assignments.some((a) => a.assessor_role === 'ATASAN')
                  const peerCount = assignments.filter((a) => a.assessor_role === 'PEER').length

                  return (
                    <tr key={employee.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 leading-tight">
                          {employee.display_name || employee.full_name || employee.email}
                        </div>
                        {employee.division_name && (
                          <div className="text-xs text-gray-400">{employee.division_name}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {hasAtasan ? (
                          <span className="text-green-600 text-xs">✓ Ada</span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {peerCount > 0 ? (
                          <span className="text-blue-600 text-xs">{peerCount} peer</span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {total === 0 ? (
                          <span className="text-gray-300 text-xs">Belum ada penugasan</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full ${
                                  pct === 100 ? 'bg-green-500' : 'bg-blue-400'
                                }`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-600 w-12 text-right">
                              {done}/{total}
                            </span>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

// ─── helper components ────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
  highlight,
}: {
  label: string
  value: string | number
  sub?: string
  highlight?: boolean
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="text-xs text-gray-500 font-medium mb-1">{label}</div>
      <div className={`text-2xl font-bold ${highlight ? 'text-green-600' : 'text-gray-900'}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  )
}

function ActionCard({
  icon,
  title,
  description,
  href,
  badge,
  disabled,
}: {
  icon: React.ReactNode
  title: string
  description: string
  href: string
  badge?: string
  disabled?: boolean
}) {
  if (disabled) {
    return (
      <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-xl p-4 opacity-50 cursor-not-allowed">
        <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {icon}
          </svg>
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-500 text-sm">{title}</span>
            {badge && (
              <span className="px-1.5 py-0.5 bg-gray-100 text-gray-400 rounded text-xs">
                {badge}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-0.5">{description}</p>
        </div>
      </div>
    )
  }

  return (
    <Link
      to={href}
      className="flex items-start gap-3 bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-sm transition-all group"
    >
      <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors">
        <svg
          className="w-5 h-5 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {icon}
        </svg>
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 text-sm">{title}</span>
          {badge && (
            <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
              {badge}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
    </Link>
  )
}
